import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '../../i18n/hooks';
import { useCalculator } from '../context/CalculatorContext';
import SurebetCalculator from './SurebetCalculator';
import './CalculatorModal.css';

/**
 * CalculatorModal - Modal wrapper for the surebet calculator
 * 
 * Features:
 * - Modal open/close functionality using CalculatorContext
 * - Pre-population with surebet data when triggered
 * - Modal backdrop click and escape key handling
 * - Mobile-friendly modal behavior
 * - Focus management for accessibility
 * - Integration with surebet items
 */

const CalculatorModal = ({ className = '' }) => {
  const { t } = useTranslation();
  const { ui, actions } = useCalculator();
  const modalRef = useRef(null);

  // Don't render if not open - MUST be after all hooks
  if (!ui.isModalOpen) {
    return null;
  }

  const handleClose = () => {
    actions.closeModal();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  // Get modal title
  const getModalTitle = () => {
    if (ui.modalData && ui.modalData.event) {
      return t(
        'surebets.calculator.modalTitleWithEvent',
        {
          teamOne: ui.modalData.event.teamOne,
          teamTwo: ui.modalData.event.teamTwo,
        },
        { defaultValue: `Calculator for ${ui.modalData.event.teamOne} vs ${ui.modalData.event.teamTwo}` },
      );
    }
    return t('surebets.calculator.modalTitle', {}, { defaultValue: 'Arbitrage Calculator' });
  };

  // Get modal subtitle
  const getModalSubtitle = () => {
    if (ui.modalData && ui.modalData.event) {
      return t(
        'surebets.calculator.modalSubtitleWithEvent',
        {
          championship: ui.modalData.event.championship,
          date: ui.modalData.event.date,
          time: ui.modalData.event.time,
        },
        { defaultValue: `${ui.modalData.event.championship} • ${ui.modalData.event.date} ${ui.modalData.event.time}` },
      );
    }
    return t(
      'surebets.calculator.modalSubtitle',
      {},
      { defaultValue: 'Calculate optimal stake distribution for expected return' },
    );
  };

  return (
    <div 
      className={`calculator-modal modal-open ${className}`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="calculator-modal-title"
      aria-describedby="calculator-modal-subtitle"
      onKeyDown={handleKeyDown}
    >
      <div className="calculator-modal__backdrop"></div>
      
      <div 
        ref={modalRef}
        className="calculator-modal__container"
        tabIndex={-1}
      >
        {/* Modal Header */}
        <div className="calculator-modal__header">
          <div className="modal-header__content">
            <h2 id="calculator-modal-title" className="modal-title">
              {getModalTitle()}
            </h2>
            <p id="calculator-modal-subtitle" className="modal-subtitle">
              {getModalSubtitle()}
            </p>
          </div>
          
          <button
            className="modal-close-button"
            onClick={handleClose}
            aria-label={t('surebets.calculator.closeModal', {}, { defaultValue: 'Close calculator' })}
            type="button"
          >
            <span className="close-icon">×</span>
          </button>
        </div>

        {/* Modal Body */}
        <div className="calculator-modal__content">
          <SurebetCalculator 
            key={`${ui.modalData?.event?.id}-${ui.modalData?.marginKey}`}
            initialOdds={ui.modalData?.odds || []}
            initialMarketType={ui.modalData?.marketType || '3-way'}
            initialEventDetails={ui.modalData?.event ? {
              teamOne: ui.modalData.event.teamOne,
              teamTwo: ui.modalData.event.teamTwo,
              date: ui.modalData.event.date,
              time: ui.modalData.event.time,
              championship: ui.modalData.event.championship,
              sport: ui.modalData.event.sport
            } : null}
            onCalculationComplete={(result) => {
              actions.addToHistory(result);
            }}
          />
        </div>

        {/* Modal Footer */}
        <div className="calculator-modal__footer">
          <button
            className="button-secondary"
            onClick={handleClose}
            type="button"
          >
            {t('surebets.calculator.close', {}, { defaultValue: 'Close' })}
          </button>
          
          {ui.modalData && (
            <div className="modal-footer__info">
              <span className="info-text">
                {t(
                  'surebets.calculator.modalInfo',
                  {},
                  { defaultValue: 'Data pre-populated from the selected arbitrage opportunity' },
                )}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

CalculatorModal.propTypes = {
  className: PropTypes.string,
};

export default CalculatorModal;